import React, { ReactNode } from 'react';

import classNames from 'classnames';

import { ExternalLink } from '@uikit';

import { Close } from '@uikit/icons/Close';
import LinkIcon from '@uikit/icons/Link';
import { MarkDotted } from '@uikit/icons/MarkDotted';

import {
  REWARD_POOL_ACCESS_POLICY_TYPE,
  RewardPool,
  SelfRewardPoolAccessPolicy,
} from '@shared/api/types/rewardPools';
import { DISCORD_URL } from '@shared/constants';
import MarkCheck from '@shared/constants/icons/MarkCheck';
import { membershipTiers } from '@shared/constants/membershipTiers';
import { GENERAL_URLS } from '@shared/constants/url';
import { pluralizeWord } from '@shared/helpers/formatters';
import { formatUsd } from '@shared/helpers/number';
import { useRewardPoolAccessPoliciesQuery } from '@shared/hooks/query/rewardPools';

import * as Dialog from '@radix-ui/react-dialog';
import QueryState from '@store:web/components/common/QueryState/QueryState';

const storeBaseUrl = process.env.NEXT_PUBLIC_BASE_PATH || '';

interface RewardPoolCardWhitelistRequirementsDialogProps extends Dialog.DialogProps {
  rewardPool: RewardPool;
}

interface RewardPoolWhitelistRequirementData {
  id: string;
  title: ReactNode;
  description?: ReactNode;
  qualified: boolean;
  action?: {
    label: string;
    href: string;
  };
}

export const transformAccessPoliciesToList = (accessPolicies: SelfRewardPoolAccessPolicy[]) => {
  const list: RewardPoolWhitelistRequirementData[] = [];

  accessPolicies.forEach((policy) => {
    switch (policy.name) {
      case REWARD_POOL_ACCESS_POLICY_TYPE.WHITELIST: {
        list.push({
          id: 'whitelist',
          title: 'Wallet Whitelist',
          description: policy.description,
          qualified: policy.qualified,
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.HOT_OFFERS_CLAIMED: {
        policy.hotOffersExpanded?.forEach(({ id, name, slug }) => {
          const href = `${storeBaseUrl}/hot-offers/${slug}`;

          list.push({
            id: `hot-offer-${id}`,
            title: 'Hot Offer Claimed',
            qualified: policy.qualified,
            action: {
              label: 'Go to Hot Offer',
              href,
            },
            description: (
              <>
                Successfully claim{' '}
                <ExternalLink
                  className="text-[#D61F7B] inline-flex gap-0.5 items-center"
                  href={href}
                >
                  {name} <LinkIcon className="icon-2xs" />
                </ExternalLink>
              </>
            ),
          });
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.KARMA_LIMIT: {
        list.push({
          id: `min-karma-${policy.options.minimal_karma}`,
          title: 'Minimum Karma',
          description: `Have a minimum total karma of ${policy.options.minimal_karma} Karma Points`,
          qualified: policy.qualified,
          action: {
            label: 'Earn Karma',
            href: `${storeBaseUrl}/profile/karma-rewards-guide`,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.MEMBERSHIP_TIERS: {
        const tierNames = policy.options.membership_tiers.map(
          (tierId) => membershipTiers[tierId].name,
        );
        const allTierNamesLabel =
          tierNames.length > 1
            ? `${tierNames.slice(0, -1).join(', ')} or ${tierNames[tierNames.length - 1]}`
            : tierNames[0];

        list.push({
          id: 'membership',
          title: 'Membership Tier',
          description: `Have ${allTierNamesLabel} Membership`,
          qualified: policy.qualified,
          action: {
            label: 'Magic Membership',
            href: `${storeBaseUrl}/profile/membership/plans`,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.HOT_OFFERS_AMOUNT: {
        const claimedAmount = policy.options.claimed_amount || 0;
        const url = `${storeBaseUrl}/hot-offers`;
        list.push({
          id: 'hot-offers-amount',
          title: 'Hot Offer Claimed in Number - All Time',
          description: (
            <>
              Claim a minimum of {claimedAmount}{' '}
              <ExternalLink className="text-[#D61F7B] inline-flex gap-0.5 items-center" href={url}>
                Hot Offer{claimedAmount !== 1 ? 's' : ''} <LinkIcon className="icon-2xs" />
              </ExternalLink>
            </>
          ),
          qualified: policy.qualified,
          action: {
            label: 'Go to Hot Offers',
            href: url,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.APPLICATIONS_VOTED: {
        policy.appsExpanded?.forEach(({ id, name, slug }) => {
          const href = `${storeBaseUrl}/app/${slug}/validation`;

          list.push({
            id: `app-validation-${id}`,
            title: 'Validation - Specific Validation',
            qualified: policy.qualified,
            action: {
              label: 'Go to Validation',
              href,
            },
            description: (
              <>
                Participate in{' '}
                <ExternalLink
                  className="text-[#D61F7B] inline-flex gap-0.5 items-center"
                  href={href}
                >
                  {name} Validation <LinkIcon className="icon-2xs" />
                </ExternalLink>
              </>
            ),
          });
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.STAKED_SQR: {
        list.push({
          id: 'staked-sqr',
          title: 'Staking - Currently Staking',
          description: <>Stake minimum of {policy.options.amount} SQR amount</>,
          qualified: policy.qualified,
          action: {
            label: 'Go to Staking',
            href: GENERAL_URLS.staking,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.APPLICATIONS_VOTED_AMOUNT: {
        const href = `${storeBaseUrl}/profile/validation-tasks`;

        list.push({
          id: 'application-voted-amount',
          title: 'Validation - Number of Validations',
          description: (
            <>
              Participate in minimum {policy.options.amount}{' '}
              <ExternalLink className="text-[#D61F7B] inline-flex gap-0.5 items-center" href={href}>
                Project {pluralizeWord('Validation', policy.options.amount)}{' '}
                <LinkIcon className="icon-2xs" />
              </ExternalLink>
            </>
          ),
          qualified: policy.qualified,
          action: {
            label: 'Go to Validations',
            href,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.USER_DAILY_LOGIN_AMOUNT: {
        list.push({
          id: 'user-daily-logins',
          title: 'Daily Logins',
          description: (
            <>
              Log in daily minimum of {policy.options.amount}{' '}
              {pluralizeWord('Time', policy.options.amount)}
            </>
          ),
          qualified: policy.qualified,
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.USER_ACCOUNT_AGE: {
        list.push({
          id: 'user-account-age',
          title: 'Age of Account Magic ID',
          description: (
            <>
              Have Magic ID account minimum {policy.options.daysAmount} of day
              {policy.options.daysAmount !== 1 ? 's' : ''} old
            </>
          ),
          qualified: policy.qualified,
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.SWAP_TOKEN_TOTAL: {
        list.push({
          id: 'swap-token-total',
          title: 'Token-Specific Swap Volume',
          description: (
            <>
              Execute swaps for Token {policy.options.tokenSymbol?.toUpperCase()} with a minimum
              total volume of {policy.options.minAmount} on Magic Swap
            </>
          ),
          qualified: policy.qualified,
          action: {
            label: 'Go to Swap',
            href: process.env.NEXT_PUBLIC_SWAP_HOST || 'https://magicsquare.io/swap',
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.SWAP_TOTAL_IN_USD: {
        list.push({
          id: 'swap-token-total-in-usdt',
          title: 'General Swap Volume',
          description: (
            <>
              Complete token swaps with a cumulative total volume of at least{' '}
              {formatUsd(policy.options.minAmount)} on Magic Swap
            </>
          ),
          qualified: policy.qualified,
          action: {
            label: 'Go to Swap',
            href: process.env.NEXT_PUBLIC_SWAP_HOST || 'https://magicsquare.io/swap',
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.USER_DISCORD_ROLE: {
        list.push({
          id: 'user-discord-role',
          title: 'Discord Role',
          description: (
            <>
              Hold specified Discord role
              {/* Hold {policy.options.rolesIds.join(', ')} Discord {pluralizeWord('role', policy.options.rolesIds.length)} */}
            </>
          ),
          qualified: policy.qualified,
          action: {
            label: 'Go to Discord',
            href: DISCORD_URL,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.HOLDING_SQRP: {
        list.push({
          id: 'holding-sqrp',
          title: 'Holding X Amount SQRp',
          description: <>Hold minimum of {policy.options.amount} SQRp</>,
          qualified: policy.qualified,
          action: {
            label: 'Earn SQRp',
            href: GENERAL_URLS.staking,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.POW_OFFERS_AMOUNT: {
        const href = `${storeBaseUrl}/magic-board`;

        list.push({
          id: 'pow-offers-amount',
          title: 'Magic Board - Number of Tasks All Time',
          description: (
            <>
              Complete a minimum of {policy.options.claimed_amount}{' '}
              <ExternalLink className="text-[#D61F7B] inline-flex gap-0.5 items-center" href={href}>
                Magic Board Tasks <LinkIcon className="icon-2xs" />
              </ExternalLink>
            </>
          ),
          qualified: policy.qualified,
          action: {
            label: 'Go to Magic Board',
            href,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.POW_OFFERS_CLAIMED: {
        policy.hotOffersExpanded?.forEach(({ id, name, slug }) => {
          const href = `${storeBaseUrl}/magic-board/${slug}`;

          list.push({
            id: `pow-offer-${id}`,
            title: 'Magic Board - Specific Task',
            qualified: policy.qualified,
            action: {
              label: 'Go to Task',
              href,
            },
            description: (
              <>
                Complete the{' '}
                <ExternalLink
                  className="text-[#D61F7B] inline-flex gap-0.5 items-center"
                  href={href}
                >
                  "{name}" <LinkIcon className="icon-2xs" />
                </ExternalLink>{' '}
                Magic Board Task
              </>
            ),
          });
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.REFERRED_AMOUNT: {
        list.push({
          id: 'referred-amount',
          title: 'Refer a Friend',
          description: <>Invite minimum of {policy.options.amount} friends</>,
          qualified: policy.qualified,
          action: {
            label: 'Invite Friends',
            href: `${storeBaseUrl}/refer-a-friend`,
          },
        });

        break;
      }

      case REWARD_POOL_ACCESS_POLICY_TYPE.PROJECT_PARTICIPANT:
        list.push({
          id: 'participation-ido',
          title: 'Participate in Specific IDO',
          description: (
            <>
              Participate in{' '}
              <ExternalLink
                className="text-[#D61F7B] inline-flex gap-0.5 items-center"
                href={`${process.env.NEXT_PUBLIC_LAUNCHPAD_HOST}/projects/${
                  policy.project?.slug || ''
                }`}
              >
                {policy.project?.name || 'N/A'} <LinkIcon className="icon-2xs" />
              </ExternalLink>
            </>
          ),
          qualified: policy.qualified,
          action: {
            label: 'Go to IDO',
            href: process.env.NEXT_PUBLIC_LAUNCHPAD_HOST || '',
          },
        });

        break;

      case REWARD_POOL_ACCESS_POLICY_TYPE.DEPOSIT_VOLUME:
        list.push({
          id: 'deposit-volume',
          title: 'Volume in Deposit in All IDOs',
          description: (
            <>
              Achieve minimum volume of {policy.options.amount} USDT deposits{' '}
              <ExternalLink
                className="text-[#D61F7B] inline-flex gap-0.5 items-center"
                href={`${process.env.NEXT_PUBLIC_LAUNCHPAD_HOST}/projects`}
              >
                across all IDOs <LinkIcon className="icon-2xs" />
              </ExternalLink>
            </>
          ),
          qualified: policy.qualified,
          action: {
            label: 'Go to Launchpad',
            href: process.env.NEXT_PUBLIC_LAUNCHPAD_HOST || '',
          },
        });

        break;

      case REWARD_POOL_ACCESS_POLICY_TYPE.DEPOSIT_TURBO_CHARGE_VOLUME:
        list.push({
          id: 'turbo-charge',
          title: 'Used Turbo Charge',
          description: <>Use Turbo Charge with minimum {policy.options.amount}$ volume</>,
          qualified: policy.qualified,
          action: {
            label: 'Go to Staking',
            href: process.env.NEXT_PUBLIC_STAKING_URL || '',
          },
        });

        break;
    }
  });

  return list;
};

const RewardPoolCardWhitelistRequirementsDialog = ({
  rewardPool,
  open,
  ...props
}: RewardPoolCardWhitelistRequirementsDialogProps) => {
  const query = useRewardPoolAccessPoliciesQuery(rewardPool.id, {
    enabled: open,
  });

  const { data } = query;

  const list = data ? transformAccessPoliciesToList(data) : [];

  return (
    <Dialog.Root open={open} {...props}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay">
          <Dialog.Content aria-describedby={undefined} className="dialog dialog-md">
            <div className="dialog-header">
              <Dialog.Title className="dialog-title">Pool Requirements</Dialog.Title>

              <Dialog.Close asChild>
                <button className="dialog-close">
                  <Close className="dialog-close-icon" />
                </button>
              </Dialog.Close>
            </div>

            <div className="dialog-body">
              {!!list && (
                <ul className="flex flex-col gap-6" data-qa="requirements_list">
                  {list.map(({ id, qualified, title, description }) => {
                    return (
                      <li key={id} className="flex gap-2 lg:gap-3 items-start" data-qa={id}>
                        <span className="flex items-center justify-center shrink-0 py-0.5 lg:py-1">
                          {qualified ? (
                            <MarkCheck
                              className="text-[#209734] size-4"
                              data-qa="requirement_checked"
                            />
                          ) : (
                            <MarkDotted
                              className="text-[#9A9AAC] size-4"
                              data-qa="requirement_dotted"
                            />
                          )}
                        </span>
                        <div className="flex flex-col gap-0.5">
                          <span
                            className={classNames('text-sm font-semibold lg:text-base', {
                              'text-[#65657B]': qualified,
                              'text-[#09090B]': !qualified,
                            })}
                          >
                            {title}
                          </span>

                          {description && (
                            <span className="text-2xs leading-3 text-[#65657B] lg:text-xs font-medium">
                              {description}
                            </span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              <QueryState
                isEmpty={!data?.length}
                query={query}
                loading={
                  <div className="flex flex-col gap-6">
                    {Array.from({ length: 4 }, (_, i) => {
                      return (
                        <div key={i} className="skeleton-block h-[2.125rem] lg:h-[2.625rem]" />
                      );
                    })}
                  </div>
                }
              />
            </div>

            <div className="dialog-footer dialog-buttons">
              <Dialog.Close asChild>
                <button
                  className="msq-btn msq-btn-lg msq-btn-primary"
                  data-qa="close_requirements_button"
                >
                  Ok
                </button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default RewardPoolCardWhitelistRequirementsDialog;
